import { ConteudoAjuda } from '../tipos';

interface EntradaAjuda {
  /** Nome da tela, usado para listar o que já tem ajuda. Não carrega o conteúdo. */
  titulo: string;
  carregar: () => Promise<{ default: ConteudoAjuda }>;
}

/**
 * Registro de ajuda: caminho da rota -> conteúdo daquela tela.
 *
 * O `carregar` é um import() DINÂMICO de propósito. O texto de cada tela só é baixado quando
 * alguém abre a ajuda daquela tela - nada entra no bundle principal. Isso importa porque o
 * bundle deste projeto já passa de 900 KB e o próprio build avisa que está grande.
 *
 * O `titulo` fica FORA do arquivo de conteúdo para que a lista de "telas que já têm ajuda"
 * possa ser montada sem baixar o texto de todas elas - o que anularia o lazy load.
 *
 * A chave é o caminho normalizado (ver `normalizarRota`), então NÃO importa se a rota em
 * RoutesNames.ts termina com barra ou não - as duas grafias convivem lá.
 *
 * PARA ACRESCENTAR UMA TELA: copie um arquivo de conteúdo, reescreva os textos, e acrescente
 * uma linha aqui. O conteúdo é texto entre aspas - não precisa ser dev para escrever.
 */
export const registroAjuda: Record<string, EntradaAjuda> = {
  '/dashboard/contasreceber': {
    titulo: 'Contas a Receber',
    carregar: () => import('./contasReceber'),
  },
};

/**
 * Tira a barra final e a caixa, para casar com as duas grafias que existem em
 * RoutesNames.ts ('/dashboard/contasreceber/' e '/dashboard/pedido').
 */
export const normalizarRota = (caminho: string): string => {
  const semBarra = caminho.replace(/\/+$/, '');
  return (semBarra === '' ? '/' : semBarra).toLowerCase();
};

export const temAjudaPara = (caminho: string): boolean =>
  normalizarRota(caminho) in registroAjuda;

export const carregarAjuda = async (caminho: string): Promise<ConteudoAjuda | null> => {
  const entrada = registroAjuda[normalizarRota(caminho)];
  if (!entrada) return null;

  const modulo = await entrada.carregar();
  return modulo.default;
};

/** Nomes das telas que já têm ajuda escrita, em ordem alfabética. */
export const telasComAjuda = (): string[] =>
  Object.values(registroAjuda)
    .map((entrada) => entrada.titulo)
    .sort((a, b) => a.localeCompare(b, 'pt-BR'));
